import React from "react";
import { ViewerSettings } from "../../types/settings";
import { t } from "../../utils/i18n";
import { useViewport } from "../../hooks/useViewport";
import { withOpacity } from "../../utils/colorUtils";

interface HeaderProps {
  onSettingsClick?: () => void;
  onCloseClick?: () => void;
  onConfigureClick?: () => void;
  headerBackgroundColor?: string;
  fontFamily?: string;
  logoSrc?: string;
  companyName?: string;
  productName?: string;
  primaryTextColor?: string;
  secondaryTextColor?: string;
  iconColor?: string;
  accentColor?: string;
  isAdminView?: boolean;
  language?: ViewerSettings["language"];
  adminActions?: {
    show: boolean;
    previewLabel: "Preview" | "Update";
    isPreviewPending: boolean;
    isDeployPending: boolean;
    isPreviewDisabled: boolean;
    isDeployDisabled: boolean;
    statusMessage?: string | null;
    onPreviewClick: () => void;
    onDeployClick: () => void;
  };
}

export function Header({
  onSettingsClick,
  onCloseClick,
  onConfigureClick,
  headerBackgroundColor = "rgba(55, 55, 55, 0.85)",
  fontFamily = "Arial, sans-serif",
  logoSrc = "/weijia-construction-logo.png",
  companyName = "Weijia Construction",
  productName = "Sunshade Louver Pergola",
  primaryTextColor = "rgba(255, 255, 255, 0.95)",
  secondaryTextColor = "rgba(255, 255, 255, 0.7)",
  iconColor = "rgba(255, 255, 255, 0.8)",
  accentColor = "rgb(56, 189, 248)",
  isAdminView = false,
  language = "en",
  adminActions,
}: HeaderProps) {
  const viewport = useViewport();

  // Responsive breakpoint values
  const isMobile = viewport === "mobile";
  const isTablet = viewport === "tablet";

  // Header height and padding
  const headerHeight = isMobile ? "3rem" : isTablet ? "3.5rem" : "4rem";
  const headerPadding = isMobile
    ? "0 0.75rem"
    : isTablet
      ? "0 1.25rem"
      : "0 2rem";

  // Logo and company name sizing
  const logoHeight = isMobile ? "1.5rem" : isTablet ? "1.75rem" : "2rem";
  const companyNameFontSize = isMobile
    ? "0.875rem"
    : isTablet
      ? "1rem"
      : "1.25rem";
  const productNameFontSize = isMobile
    ? "0.75rem"
    : isTablet
      ? "0.875rem"
      : "1rem";

  // Gaps and spacing
  const contentGap = isMobile ? "0.5rem" : isTablet ? "0.75rem" : "1rem";
  const buttonContainerGap = isMobile
    ? "0.25rem"
    : isTablet
      ? "0.5rem"
      : "0.75rem";

  // Button sizing
  const buttonSize = isMobile ? "2rem" : isTablet ? "2.25rem" : "2.5rem";
  const buttonIconSize = isMobile ? "1rem" : isTablet ? "1.125rem" : "1.25rem";
  const actionButtonPadding = isMobile
    ? "0 0.65rem"
    : isTablet
      ? "0 0.8rem"
      : "0 1rem";
  const actionButtonFontSize = isMobile
    ? "0.75rem"
    : isTablet
      ? "0.8rem"
      : "0.875rem";

  // Divider height
  const dividerHeight = isMobile ? "1rem" : isTablet ? "1.25rem" : "1.5rem";
  const logoAlt =
    language === "es"
      ? "Logotipo de la empresa"
      : language === "fr"
        ? "Logo de l\u2019entreprise"
        : language === "de"
          ? "Firmenlogo"
          : language === "zh"
            ? "\u516c\u53f8\u6807\u5fd7"
            : "Company logo";
  const isPreviewButtonDisabled = Boolean(
    adminActions?.isPreviewPending || adminActions?.isPreviewDisabled,
  );
  const isDeployButtonDisabled = Boolean(
    adminActions?.isDeployPending || adminActions?.isDeployDisabled,
  );

  return (
    <header
      style={{
        position: "relative",
        zIndex: 110,
        width: "100%",
        height: headerHeight,
        padding: headerPadding,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: headerBackgroundColor,
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
        fontFamily,
      }}
      data-oid="s.yp-cy"
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: contentGap,
          minWidth: 0,
          flex: 1,
        }}
        data-oid="x3y:7ei"
      >
        <img
          src={logoSrc}
          alt={logoAlt}
          style={{
            height: logoHeight,
            width: "auto",
            objectFit: "contain",
            display: "block",
            flexShrink: 0,
          }}
          data-oid="fpm0s90"
        />

        <span
          style={{
            fontSize: companyNameFontSize,
            fontWeight: 700,
            color: primaryTextColor,
            lineHeight: 1,
            fontFamily,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          data-oid="gu-h:xg"
        >
          {companyName}
        </span>

        <div
          style={{
            width: "1px",
            height: dividerHeight,
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            flexShrink: 0,
          }}
          data-oid="15afewq"
        />

        <h1
          style={{
            margin: 0,
            fontSize: productNameFontSize,
            fontWeight: 500,
            color: secondaryTextColor,
            lineHeight: 1,
            fontFamily,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          data-oid="wdx_a44"
        >
          {productName}
        </h1>

        {isAdminView && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: isMobile
                ? "0.1rem 0.35rem"
                : isTablet
                  ? "0.15rem 0.4rem"
                  : "0.2rem 0.5rem",
              borderRadius: "9999px",
              border: "1px solid rgba(250, 204, 21, 0.45)",
              background: "rgba(250, 204, 21, 0.15)",
              color: "rgba(254, 240, 138, 0.95)",
              fontSize: isMobile ? "0.5rem" : isTablet ? "0.6rem" : "0.6875rem",
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              lineHeight: 1,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
            aria-label={t(language, "header.adminModeEnabled")}
            title={t(language, "header.administrativeView")}
            data-oid="mgv.fd_"
          >
            {t(language, "header.adminMode")}
          </span>
        )}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: buttonContainerGap,
          flexShrink: 0,
        }}
        data-oid="nq5tr40"
      >
        {adminActions?.show ? (
          <>
            <button
              type="button"
              onClick={adminActions.onPreviewClick}
              disabled={isPreviewButtonDisabled}
              aria-label={adminActions.previewLabel}
              title={adminActions.previewLabel}
              style={{
                height: buttonSize,
                padding: actionButtonPadding,
                borderRadius: "9999px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ffffff",
                background: "rgba(255, 255, 255, 0.12)",
                border: "1px solid rgba(255, 255, 255, 0.18)",
                cursor: isPreviewButtonDisabled
                  ? adminActions.isPreviewPending
                    ? "progress"
                    : "not-allowed"
                  : "pointer",
                transition: "all 0.3s",
                fontFamily,
                fontSize: actionButtonFontSize,
                fontWeight: 600,
                opacity: isPreviewButtonDisabled ? 0.65 : 1,
              }}
              data-oid="z:z6yj."
            >
              {adminActions.previewLabel}
            </button>

            <button
              type="button"
              onClick={adminActions.onDeployClick}
              disabled={isDeployButtonDisabled}
              aria-label="Deploy"
              title="Deploy"
              style={{
                height: buttonSize,
                padding: actionButtonPadding,
                borderRadius: "9999px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#101828",
                background: "#ffffff",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                cursor: isDeployButtonDisabled
                  ? adminActions.isDeployPending
                    ? "progress"
                    : "not-allowed"
                  : "pointer",
                transition: "all 0.3s",
                fontFamily,
                fontSize: actionButtonFontSize,
                fontWeight: 700,
                opacity: isDeployButtonDisabled ? 0.65 : 1,
              }}
              data-oid="f_sl8wq"
            >
              Deploy
            </button>
          </>
        ) : null}

        {adminActions?.show && adminActions.statusMessage ? (
          <div
            aria-live="polite"
            style={{
              maxWidth: isMobile ? "10rem" : isTablet ? "14rem" : "18rem",
              padding: isMobile ? "0.35rem 0.55rem" : "0.4rem 0.7rem",
              borderRadius: "0.75rem",
              border: "1px solid rgba(255, 255, 255, 0.14)",
              background: "rgba(12, 12, 12, 0.45)",
              color: secondaryTextColor,
              fontSize: isMobile ? "0.625rem" : "0.7rem",
              lineHeight: 1.35,
            }}
            data-oid="1kg-ok8"
          >
            {adminActions.statusMessage}
          </div>
        ) : null}

        {isAdminView && onConfigureClick && (
          <button
            type="button"
            onClick={onConfigureClick}
            aria-label="Configure viewer"
            title="Configure"
            style={{
              height: buttonSize,
              padding: actionButtonPadding,
              borderRadius: "9999px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: withOpacity(accentColor, 0.95),
              background: withOpacity(accentColor, 0.1),
              border: `1px solid ${withOpacity(accentColor, 0.25)}`,
              cursor: "pointer",
              transition: "all 0.3s",
              fontFamily,
              fontSize: actionButtonFontSize,
              fontWeight: 600,
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: buttonIconSize, marginRight: '0.25rem' }}>
              tune
            </span>
            {!isMobile && 'Configure'}
          </button>
        )}

        <button
          onClick={onSettingsClick}
          aria-label={t(language, "header.settings")}
          style={{
            width: buttonSize,
            height: buttonSize,
            borderRadius: "9999px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: iconColor,
            background: "transparent",
            border: "1px solid transparent",
            cursor: "pointer",
            transition: "all 0.3s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "#ffffff";
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
            e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.2)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = iconColor;
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.borderColor = "transparent";
          }}
          title={t(language, "header.settings")}
          data-oid=":w_1jb:"
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: buttonIconSize }}
            data-oid="gduyth3"
          >
            settings
          </span>
        </button>

        <button
          onClick={onCloseClick}
          aria-label={t(language, "header.close")}
          style={{
            width: buttonSize,
            height: buttonSize,
            borderRadius: "9999px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: iconColor,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            transition: "all 0.3s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "#f43f5e";
            e.currentTarget.style.background = "rgba(244, 63, 94, 0.1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = iconColor;
            e.currentTarget.style.background = "transparent";
          }}
          title={t(language, "header.close")}
          data-oid="n3m445n"
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: buttonIconSize }}
            data-oid="79je0m8"
          >
            close
          </span>
        </button>
      </div>
    </header>
  );
}
